import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { AuthHttpService, AuthService, AuthSimuladoService, sesionExpiradaInterceptor } from '@auth';
import { environment } from 'src/environments/environment';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';

/**
 * En produccion NO se cae al simulado: se LANZA.
 *
 * Un build productivo que aceptara `demo/demo` es un incidente, no un bug — y en
 * el login CENTRAL de la plataforma seria la puerta de todos los sistemas a la
 * vez. Esto es la segunda red; la primera es R5 de `pnpm run audit`, que corre
 * en CI. Existe porque el `useClass` de abajo no falla por si solo: proveeria el
 * simulado tan campante.
 */
if (environment.production && environment.authSimulado) {
  throw new Error(
    'authSimulado esta activo en un build de produccion. Revisa environments/environment.prod.ts.',
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      // El `volverA` tiene que sobrevivir a la redireccion del `**` al login:
      // sin esto, quien llega a una ruta desconocida con el parametro puesto lo
      // pierde y acaba en la rejilla en vez de en su destino.
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideBrowserGlobalErrorListeners(),
    // ⚠️ El orden importa: `credentials` marca la peticion ANTES de que
    // `sesionExpirada` pueda reintentarla tras refrescar, asi que el reintento
    // conserva `withCredentials` y el navegador adjunta la cookie NUEVA.
    //
    // Y `credentialsInterceptor` no es opcional en ningun caso: sin el, el
    // navegador no adjunta la cookie y TODO responde 401 aunque la sesion este
    // perfectamente abierta en el servidor. No hay nada que lo delate.
    provideHttpClient(withInterceptors([credentialsInterceptor, sesionExpiradaInterceptor])),
    provideAnimationsAsync(),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // El idioma de la plataforma (ui >= 5.2.10): registra `es-MX` y alinea con el
    // a Angular (`LOCALE_ID`) y a ng-zorro (`provideNzI18n` con `locale: 'es-MX'`).
    // Sustituye a las tres lineas a mano, que podian desalinearse: con
    // `es_ES.locale` (`'es'`) y solo `es-MX` registrado, el calendario de todo
    // date picker truena con `NG0701` y sale un panel vacio.
    proveerI18n(),
    // `AuthService` es una clase ABSTRACTA, no una interfaz: una interfaz
    // desaparece al compilar y no sirve como token de inyeccion.
    {
      provide: AuthService,
      useClass: environment.authSimulado ? AuthSimuladoService : AuthHttpService,
    },
    // Rehidrata la sesion ANTES del primer render. Sin esto, un F5 en la rejilla
    // pinta el formulario de credenciales por un instante y el parpadeo se lee
    // como un cierre de sesion espurio.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restaurarSesion())),
  ],
};
